import { Button, Flex, Heading, Image, Link, Skeleton, Text } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { APP_CRASHED, Mixpanel } from 'libs/third-party/Mixpanel';
import { ZendeskProvider, useZendesk } from 'libs/third-party/Zendesk/zendeskProvider';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteError } from 'react-router-dom';

export const ErrorPage = () => {
  return (
    <ZendeskProvider>
      <ErrorPageContent />
    </ZendeskProvider>
  );
};

const IMAGE_WIDTH = '140px';
const IMAGE_HEIGHT = '137px';
const IMAGE_URL = import.meta.env.VITE_CDN_DOMAIN + '/media/e2-assets/vg_error_image.png';
const releaseVersion = import.meta.env.VITE_RELEASE_VERSION || '1';

const ErrorPageContent = () => {
  const { t } = useTranslation('error_page', { useSuspense: false });
  const { show } = useZendesk();
  const onClickRefresh = () => window.location.reload();
  const location = useLocation();

  const error = useRouteError();
  const errorReported = useRef(false);

  useEffect(() => {
    if (error && !errorReported.current) {
      Sentry.withScope((scope) => {
        scope.setLevel('fatal');
        // Extract base route path (e.g. '/design' instead of '/design/123')
        const basePath = '/' + location.pathname.split('/')[1];

        if (error instanceof Error) {
          // Create fingerprint to group similar errors across releases
          // Format: [error type, error message, route path]
          // Example: ['TypeError', 'Cannot read property map of null', '/design']
          const fingerPrint = [error.name, error.message, basePath];
          scope.setFingerprint(fingerPrint);

          Sentry.captureException(error);
          Mixpanel.track(APP_CRASHED, {
            errorName: error.name,
            errorMessage: error.message,
            page: basePath,
            releaseVersion,
          });
        } else {
          Sentry.captureException(new Error('Unknown error detected'));
          Mixpanel.track(APP_CRASHED, { errorName: 'Unkown Error', message: '', page: basePath, releaseVersion });
        }
      });
      errorReported.current = true;
    }
  }, [error, location]);

  return (
    <Flex bg='blue.50' minHeight='100vh' minW='100vw' justify='center' align='center'>
      <Flex direction='column' gap='8' justify='center' align='center' maxW='772px'>
        <Image
          fallback={<Skeleton color='white' w={IMAGE_WIDTH} h={IMAGE_HEIGHT} />}
          src={IMAGE_URL}
          alt=''
          w={IMAGE_WIDTH}
          h={IMAGE_HEIGHT}
          role='presentation'
        />
        <Heading color='font.500' as='h1' fontSize='5xl' textAlign='center' m={0}>
          {t('errorTitle')}
        </Heading>
        <Text textAlign='center' color='font.500'>
          {t('errorText')}{' '}
          <Link as='button' textDecor='underline' onClick={show}>
            {t('errorLink')}
          </Link>
          .
        </Text>
        <Button onClick={onClickRefresh} variant='upgrade' size='sm'>
          {t('refresh')}
        </Button>
      </Flex>
    </Flex>
  );
};
